import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import app from '../../firebase.js';

// Import images
import ethosLogo from "../../UNCR/img/ethos.jpg";
import cirepLogo from "../../UNCR/img/cirep.jpg";
import dphuLogo from "../../UNCR/img/dphulogo.png";
import ethosBackground from "../../UNCR/img/ethos-background.png";
import bapSig from "../../UNCR/img/bap_sig.png";
import rectorSig from "../../UNCR/img/rector.png";
import './EthosCertificate.css';
import ethosSig from "../../UNCR/img/ethos.png";

const EthosCertificateTemplate = () => {
    const [formData, setFormData] = useState({
        nom: '',
        dateNaissance: '',
        lieuNaissance: '',
        paysConnector: 'in',
        pays: '',
        topic: '',
        conferenceTitle: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `ethos-certificate/EDAP/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `ETHOS CERTIFICATE OF SCIENTIFIC COMMUNICATION\n`;
            qrData += `Name: ${selectedDocument.nom || ''}\n`;
            qrData += `Date of Birth: ${selectedDocument.dateNaissance || ''}\n`;
            qrData += `Place of Birth: ${selectedDocument.lieuNaissance || ''}\n`;
            qrData += `Country: ${selectedDocument.pays || ''}\n`;
            qrData += `Topic: ${selectedDocument.topic || ''}\n`;
            qrData += `Conference: ${selectedDocument.conferenceTitle || ''}\n`;
            qrData += `Issue Date: ${selectedDocument.dateEmission || ''}\n`;
            qrData += `Status: Document Verified\n`;
            qrData += `Issued by: Ethos Universitas, DPHU, CIREP`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const requiredFields = ['nom', 'dateNaissance', 'lieuNaissance', 'pays', 'topic', 'conferenceTitle', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        // Save to Firebase
        const documentRefPath = isEditing 
            ? ref(db, `ethos-certificate/EDAP/${editingId}`)
            : ref(db, `ethos-certificate/EDAP/${formData.nom.replace(/\s+/g, '_')}_${Date.now()}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                console.log(isEditing ? 'Ethos certificate updated successfully' : 'Ethos certificate saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving Ethos certificate:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            nom: '',
            dateNaissance: '',
            lieuNaissance: '',
            paysConnector: 'in',
            pays: '',
            topic: '',
            conferenceTitle: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            nom: document.nom || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            paysConnector: document.paysConnector || 'in',
            pays: document.pays || '',
            topic: document.topic || '',
            conferenceTitle: document.conferenceTitle || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0]
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `ethos-certificate/EDAP/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('ethos-certificate-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        try {
            const pdf = new jsPDF('l', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth(); // 842pt
            const pageHeight = pdf.internal.pageSize.getHeight(); // 595pt

            // Get the document page
            const page = element.querySelector('.ethos-certificate-page');
            
            if (!page) {
                message.error('Aucune page trouvée');
                return;
            }

            // Ensure white background for capture
            page.style.backgroundColor = 'white';

            // Wait for images to load
            const images = page.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => new Promise((resolve) => {
                if (img.complete) resolve();
                else { img.onload = resolve; img.onerror = resolve; }
            })));

            await new Promise(resolve => setTimeout(resolve, 400));

            const canvas = await html2canvas(page, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                windowWidth: page.scrollWidth,
                windowHeight: page.scrollHeight
            });

            // Calculate scaling to fit page properly
            const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
            const w = canvas.width * ratio;
            const h = canvas.height * ratio;
            const x = (pageWidth - w) / 2; // Center horizontally
            const y = 20; // Small top margin

            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
            pdf.save(`${selectedDocument.nom?.replace(/\s+/g, '_') || 'document'}_Ethos_Certificate.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        }
    };

    return (
        <div className="ethos-certificate-container">
            <div className="ethos-certificate-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={ethosLogo} alt="Logo Ethos" className="ethos-certificate-form-logo" />
                    <h2>Formulaire de Certificat de Communication Scientifique - Ethos</h2>
                </div>

                <form onSubmit={handleSubmit} className="ethos-certificate-form">
                    {/* Informations personnelles */}
                    <div className="ethos-certificate-form-section-title">Informations personnelles</div>
                    <div className="ethos-certificate-form-row">
                        <input
                            type="text"
                            name="nom"
                            value={formData.nom}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                            className="ethos-certificate-form-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="ethos-certificate-form-input"
                        />
                    </div>

                    <div className="ethos-certificate-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                            className="ethos-certificate-form-input"
                        />
                        <input
                            type="text"
                            name="paysConnector"
                            value={formData.paysConnector}
                            onChange={handleInputChange}
                            placeholder="Connecteur (ex: in, à, etc.)"
                            className="ethos-certificate-form-input"
                        />
                    </div>

                    <div className="ethos-certificate-form-row">
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="ethos-certificate-form-input"
                        />
                    </div>

                    {/* Informations de communication */}
                    <div className="ethos-certificate-form-section-title">Informations de communication</div>
                    <div className="ethos-certificate-form-row">
                        <textarea
                            name="topic"
                            value={formData.topic}
                            onChange={handleInputChange}
                            placeholder="Sujet de la communication"
                            required
                            className="ethos-certificate-form-input"
                            rows="3"
                            style={{ gridColumn: '1 / -1' }}
                        />
                    </div>

                    <div className="ethos-certificate-form-row">
                        <textarea
                            name="conferenceTitle"
                            value={formData.conferenceTitle}
                            onChange={handleInputChange}
                            placeholder="Titre de la conférence"
                            required
                            className="ethos-certificate-form-input"
                            rows="2"
                            style={{ gridColumn: '1 / -1' }}
                        />
                    </div>

                    {/* Informations du document */}
                    <div className="ethos-certificate-form-section-title">Informations du document</div>
                    <div className="ethos-certificate-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="ethos-certificate-form-input"
                        />
                    </div>

                    <button type="submit" className="ethos-certificate-submit-btn">
                        {isEditing ? 'Modifier le Certificat Ethos' : 'Créer le Certificat Ethos'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="ethos-certificate-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="ethos-certificate-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="ethos-certificate-document-item">
                            <button
                                className="ethos-certificate-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.nom} - Ethos Certificate
                            </button>
                            <button
                                className="ethos-certificate-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="ethos-certificate-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Certificat de Communication Scientifique Ethos</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="ethos-certificate-modal-content">
                            <EthosCertificateDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const EthosCertificateDocument = ({ document: docData, qrCodeData }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <div id="ethos-certificate-document" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            width: 'fit-content',
            minWidth: '30cm',
            margin: '0 auto',
            overflowX: 'hidden'
        }}>
            <div className="ethos-certificate-page" style={{
                width: '29.7cm',
                height: '21cm',
                padding: '2cm',
                margin: '0 auto 30px auto',
                background: 'white',
                color: 'black',
                position: 'relative',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.6,
                pageBreakAfter: 'always',
                pageBreakInside: 'avoid',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                fontFamily: "'Times New Roman', serif"
            }}>
                {/* Background Image */}
                <img 
                    src={ethosBackground} 
                    alt="Background" 
                    className="ethos-certificate-background"
                    style={{
                        position: 'absolute',
                        top: '0cm',
                        left: '0.2cm',
                        width: 'calc(100% - 0.5cm)',
                        height: 'calc(100% - 1cm)',
                        objectFit: 'contain',
                        zIndex: 0,
                        pointerEvents: 'none'
                    }}
                />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', marginLeft: '-0.8cm' }}>
                    {/* Header with Logos */}
                    <div className="ethos-certificate-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '30px',
                        marginLeft: '-0.2cm',
                        marginTop: '-20px',
                        padding: '0 0.5cm'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={ethosLogo} alt="Ethos Logo" style={{ maxWidth: '300px', maxHeight: '230px', objectFit: 'contain', zIndex: 2, position: 'relative' }} />
                        </div>
                        <div style={{ width: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={cirepLogo} alt="CIREP Logo" style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain', borderRadius: '8px', zIndex: 2, position: 'relative' }} />
                        </div>
                        <div style={{ width: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={dphuLogo} alt="DPHU Logo" style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain', zIndex: 2, position: 'relative' }} />
                        </div>
                    </div>

                    {/* Title */}
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            margin: 0,
                            color: '#000'
                        }}>
                            CERTIFICATE OF SCIENTIFIC COMMUNICATION
                        </h1>
                    </div>

                    {/* Body Text */}
                    <div className="ethos-certificate-body" style={{
                        flex: 1,
                        fontSize: '14px',
                        lineHeight: 1.8,
                        marginBottom: '-50px',
                        padding: '0 0.5cm',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <p style={{ marginBottom: '15px', textAlign: 'justify' }}>
                            Aligning with a growing global alliance of higher education institutions and communities united in their commitment to ethics and integrity in academia and communities,
                        </p>
                        <p style={{ marginBottom: '15px', textAlign: 'justify' }}>
                            In Accordance with the partnership between Ethos Universitas ; Panathlon International University Platform in Belgium; Distant Production House University (DPHU) and Lisala University / Interuniversity Center for Multidisciplinary Research(CIREP) in Democratic Republic of Congo
                        </p>
                        <p style={{ marginBottom: '15px', textAlign: 'justify' }}>
                            We certify that <strong>{docData.nom || ''}</strong>, born on <strong>{formatDate(docData.dateNaissance)}</strong> at <strong>{docData.lieuNaissance || ''}</strong> {docData.paysConnector || 'in'} <strong>{docData.pays || ''}</strong>;
                        </p>
                        <p style={{ marginBottom: '15px', textAlign: 'justify' }}>
                            successfully made a communication on the topic <strong>"{docData.topic || ''}"</strong> during the doctoral program conference titled <strong>"{docData.conferenceTitle || ''}"</strong>
                        </p>
                    </div>

                    {/* Signatures */}
                    <div className="ethos-certificate-signatures" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        marginTop: 'auto',
                        paddingTop: '10px',
                        marginLeft: '-1.5cm',
                        padding: '10px 0.5cm 0 0.5cm',
                        position: 'relative',
                        zIndex: 1,
                        transform: 'translate(0, -20px)'  
                    }}>
                        <div style={{ flex: 1, textAlign: 'center', margin: '0 10px' }}>
                            <div style={{ height: '60px', marginBottom: '10px' }}></div>
                            <img src={ethosSig} alt="Ethos Logo" style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain', zIndex: 2, position: 'relative' }} />
                            <hr style={{ width: '200px', margin: '0 auto 8px auto', border: 'none', borderTop: '1px solid #000' }} />
                            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '4px 0', color: '#000' }}>
                                Prof. Dr. Em. Francis Van Loon, Baron
                            </p>
                            <p style={{ fontSize: '11px', margin: '2px 0', color: '#000' }}>
                                Ethos Universitas President
                            </p>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', margin: '0 10px' }}>
                            <img 
                                src={bapSig} 
                                alt="Signature" 
                                style={{ 
                                    width: '120px', 
                                    height: '60px', 
                                    objectFit: 'contain', 
                                    marginBottom: '8px',
                                    display: 'block',
                                    margin: '0 auto 8px auto'
                                }} 
                            />
                            <hr style={{ width: '200px', margin: '0 auto 8px auto', border: 'none', borderTop: '1px solid #000' }} />
                            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '4px 0', color: '#000' }}>
                                Prof NIZEYIMANA Jean Baptiste
                            </p>
                            <p style={{ fontSize: '11px', margin: '2px 0', color: '#000' }}>
                                Rector of DPHU
                            </p>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', margin: '0 10px', position: 'relative' }}>
                            <img 
                                src={rectorSig} 
                                alt="Signature" 
                                style={{ 
                                    width: '120px', 
                                    height: '60px', 
                                    objectFit: 'contain', 
                                    marginBottom: '8px',
                                    display: 'block',
                                    margin: '0 auto 8px auto'
                                }} 
                            />
                            <hr style={{ width: '200px', margin: '0 auto 8px auto', border: 'none', borderTop: '1px solid #000' }} />
                            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '4px 0', color: '#000' }}>
                                Prof Abbé Donat ALOMO TEBAKABE
                            </p>
                            <p style={{ fontSize: '11px', margin: '2px 0', color: '#000' }}>
                                Rector of Lisala University
                            </p>
                            {qrCodeData && (
                                <div style={{
                                    position: 'absolute',
                                    right: '-60px',
                                    bottom: '0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    zIndex: 2
                                }}>
                                    <img
                                        src={qrCodeData}
                                        alt="QR Code"
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            border: '1px solid #000',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EthosCertificateTemplate;

